import React, { useState } from "react";
import { ArrowDownUp, List, Trash } from "react-bootstrap-icons";
import { Col, Dropdown, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { weekendApi } from "../../../../api/weekend";
import { HGBugsnagNotify } from "../../../../helpers/bugsnag";
import { AssignmentNotification } from "../../../../types/scheduling/meetings";
import { Congregation, PublicTalkAssignment, Speaker } from "../../../../types/scheduling/weekend";
import { NotificationDates } from "../../../../types/date";
import { CongSettings } from "../../../../types/scheduling/settings";
import { AssignmentSummary } from "../../RecentAssignments";
import { SaveResult, SaveStatus } from "../../../saveStatus";
import QueryKeys from "../../../../api/queryKeys";
import { OutSpeakerDropdown } from "./dropdowns/OutSpeakerDropdown";
import { CongregationDropdown } from "./dropdowns/CongregationDropdown";
import { TalkDropdown } from "./dropdowns/TalkDropdown";
import { congCompare } from "../../common";

interface OutgoingAssignmentProps {
  out: PublicTalkAssignment;
  langGroupId: number;
  setOut: (set: PublicTalkAssignment) => void;
  speakers: Speaker[];
  congregations: Congregation[];
  setSelectedID: (set: number) => void;
  setShowDeleteConfirm: (set: boolean) => void;
  notifications?: AssignmentNotification[];
  notificationDates: NotificationDates;
  setShowMoveModal: (set: boolean) => void;
  congSettings: CongSettings;
  userAssignmentMap: Map<number, AssignmentSummary[]>;
  pwSchedules: any[];
  absences?: any[];
}

export const OutgoingAssignment: React.FC<OutgoingAssignmentProps> = (props) => {
  const { t } = useTranslation();
  const [speaker, setSpeaker] = useState(props.out.speaker?.id ?? 0);
  const [talk, setTalk] = useState(props.out.public_talk?.number ?? 0);
  const [congId, setCongId] = useState(props.out.congregation?.id ?? 0);
  const [saveResult, setSaveResult] = useState(SaveResult.None);
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();

  const meetingDate = weekOfString(props.out.date, props.congregations.find((c) => c.id === congId)?.wm_dow ?? 7);

  const save = async (speaker: number, cong: number, talk: number, force?: boolean) => {
    // force lets the cong save happen regardless of whether anything else is specified
    if (!force && (!talk || !cong || !speaker)) return;
    setSaving(true);
    try {
      await weekendApi.setOutSchedule(
        {
          id: props.out.id,
          date: props.out.date,
          assignee: speaker,
          congregation: cong,
          talk: talk,
        },
        props.langGroupId,
      );
      setSaveResult(SaveResult.Success);
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.WMCSchedules],
      });
    } catch (err: any) {
      setSaveResult(SaveResult.Failure);
      console.error("wm outgoing save error", err);
      HGBugsnagNotify("wmOutgoingSave", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-3 row p-0" key={props.out.id}>
      <Row>
        <Col lg={4}>
          <OutSpeakerDropdown
            className="d-flex flex-column"
            speakers={props.speakers}
            absences={props.absences}
            out={props.out}
            meetingDate={meetingDate}
            selected={speaker}
            saving={saving}
            notifications={props.notifications}
            notificationDates={props.notificationDates}
            congSettings={props.congSettings}
            userAssignmentMap={props.userAssignmentMap}
            outCongregation={props.congregations.find((c) => c.id === congId)}
            pwSchedules={props.pwSchedules}
            setSpeaker={(speaker) => {
              //when we change the speaker, we still need to know what talk, so we don't save it
              setSpeaker(speaker);
              setTalk(0);
            }}
          />
        </Col>
        <Col lg={4}>
          <CongregationDropdown
            congs={props.congregations.sort(congCompare)}
            excludeMyCong
            langGroupId={props.langGroupId}
            selected={congId}
            setCong={async (cong) => {
              setCongId(cong);
              if (cong) {
                // as long as we have a cong selected, save it
                await save(speaker, cong, talk, true);
              }
            }}
          />
        </Col>
        <Col lg={3}>
          <TalkDropdown
            speakers={props.speakers}
            disabled={!canUpdateWeekendSchedules()}
            selectedSpeaker={speaker}
            selectedTalk={talk}
            setTalk={async (talk) => {
              setTalk(talk);
              await save(speaker, congId, talk);
            }}
            date={meetingDate}
          />
        </Col>
        <Col lg={1}>
          <Dropdown className="float-end">
            <Dropdown.Toggle variant="secondary" className="py-2 d-flex align-items-center" size="sm">
              {saveResult === SaveResult.None ? (
                <List />
              ) : (
                <SaveStatus
                  noSpace
                  size={13.5}
                  saveResult={saveResult}
                  saveKey={props.out.id}
                  setSaveResult={setSaveResult}
                />
              )}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                disabled={!canUpdateWeekendSchedules()}
                onClick={() => {
                  props.setOut(props.out);
                  props.setShowMoveModal(true);
                }}
              >
                <ArrowDownUp /> {t("schedules.weekend.move-assignment")}
              </Dropdown.Item>
              <Dropdown.Item
                disabled={!canUpdateWeekendSchedules()}
                onClick={() => {
                  props.setSelectedID(props.out.id);
                  props.setShowDeleteConfirm(true);
                }}
              >
                <Trash color="red" /> {t("schedules.weekend.delete-outgoing")}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Col>
      </Row>
    </div>
  );
};

// Helper functions and constants that need to be imported
import { weekOfString } from "../../../../helpers/dateHelpers";
import { canUpdateWeekendSchedules } from "../wm_common";
